import { PLAN_BERJUANG_MAX } from '@iqrapath/shared'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Obj } from '../../components/Obj.tsx'
import { ApiError, putPlan } from '../../lib/api.ts'
import { BERJUANG, JADI, MENIT } from './plan.ts'

/** The three questions of "Rencana", shared by onboarding (/mulai/rencana) and Perjalanan → Rencana. */
export function PlanFields({ berjuang, jadi, menit, onBerjuang, onJadi, onMenit }: {
  berjuang: string[]
  jadi: string | null
  menit: number | null
  onBerjuang: (v: string[]) => void
  onJadi: (v: string) => void
  onMenit: (v: number) => void
}) {
  const full = berjuang.length >= PLAN_BERJUANG_MAX
  const toggle = (k: string) => onBerjuang(berjuang.includes(k) ? berjuang.filter((x) => x !== k) : berjuang.length < PLAN_BERJUANG_MAX ? [...berjuang, k] : berjuang)
  return (
    <>
      <fieldset className="q">
        <legend className="section-t">Lagi berjuang di mana? <span className="q-hint">pilih sampai {PLAN_BERJUANG_MAX}</span></legend>
        <div className="tiles">
          {BERJUANG.map((o) => {
            const on = berjuang.includes(o.key)
            return (
              <label key={o.key} className="card tile" data-off={full && !on ? '' : undefined}>
                <input className="sr" type="checkbox" checked={on} disabled={full && !on} onChange={() => toggle(o.key)} />
                <Obj id={o.obj} size={44} />
                <span>{o.label}</span>
                <span className="tick" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <fieldset className="q">
        <legend className="section-t">Ingin jadi pribadi yang…</legend>
        <div className="tiles">
          {JADI.map((o) => (
            <label key={o.key} className="card tile">
              <input className="sr" type="radio" name="jadi" checked={jadi === o.key} onChange={() => onJadi(o.key)} />
              <Obj id={o.obj} size={44} />
              <span>{o.label}</span>
              <span className="tick" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="q">
        <legend className="section-t">Berapa menit sehari?</legend>
        <div className="seg">
          {MENIT.map((m) => (
            <label key={m} className="seg-opt">
              <input className="sr" type="radio" name="menit" checked={menit === m} onChange={() => onMenit(m)} />
              <span>{m} menit</span>
            </label>
          ))}
        </div>
      </fieldset>
    </>
  )
}

/** /mulai/rencana: optional onboarding step 2 (docs/PRD.md US-2.4). Saved for Perjalanan; "Nanti aja" skips it. */
export function PlanPage() {
  const [berjuang, setBerjuang] = useState<string[]>([])
  const [jadi, setJadi] = useState<string | null>(null)
  const [menit, setMenit] = useState<number | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const valid = berjuang.length > 0 && jadi !== null && menit !== null

  const save = async () => {
    setError('')
    setPending(true)
    try {
      await putPlan({ berjuang, jadi, menit })
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Terjadi masalah. Coba lagi nanti.')
    } finally {
      setPending(false)
    }
  }

  if (done) {
    const label = (list: { key: string; label: string }[], k: string | null) => list.find((x) => x.key === k)?.label
    return (
      <main className="consent onb">
        <Obj id="tunas" size={88} className="empty-obj onb-done-obj" />
        <h1 style={{ marginTop: 10 }}>Rencanamu tersimpan</h1>
        <div className="card plan-sum">
          <p><span className="muted">Lagi berjuang:</span> {berjuang.map((k) => label(BERJUANG, k)).join(' · ')}</p>
          <p><span className="muted">Ingin jadi:</span> {label(JADI, jadi)}</p>
          <p><span className="muted">Waktu:</span> {menit} menit sehari</p>
        </div>
        <p className="muted" style={{ marginTop: 14 }}>
          Untuk sekarang, mulai dari satu ayat hari ini. Saran kebiasaan dan tantangan 7 hari yang cocok akan muncul di Perjalanan.
        </p>
        <Link className="btn btn-primary btn-block" to="/" replace style={{ marginTop: 20, minHeight: 54 }}>Lihat ayat hari ini</Link>
      </main>
    )
  }

  return (
    <main className="consent onb">
      <p className="eyebrow">Langkah 2 dari 2 · boleh dilewati</p>
      <h1 style={{ marginTop: 6 }}>Rencana kecilmu</h1>
      <p className="muted" style={{ marginTop: 8 }}>Tiga pertanyaan singkat, supaya saran di Perjalanan terasa pas. Tidak ada jawaban yang salah.</p>

      <PlanFields berjuang={berjuang} jadi={jadi} menit={menit} onBerjuang={setBerjuang} onJadi={setJadi} onMenit={setMenit} />

      <p className="error" role="alert">{error}</p>
      <button className="btn btn-primary btn-block" type="button" onClick={save} disabled={!valid || pending} style={{ minHeight: 54 }}>
        {pending ? <><span className="spinner" aria-hidden="true" /> Menyimpan…</> : 'Simpan rencana'}
      </button>
      {!valid && <p className="help" style={{ textAlign: 'center' }}>Jawab ketiganya dulu, atau lewati.</p>}
      <Link className="btn btn-ghost btn-block" to="/" replace style={{ marginTop: 10 }}>Nanti aja</Link>
    </main>
  )
}
